/**
 * The marks a listing carries on its card and on its page: «مميز» (a paid
 * placement), then the marks a listing earns from what visitors do with it.
 *
 * Owner, 2026-10-07: the marks existed (the property page header and the
 * similar-listings cards) but none ever showed. «الأكثر طلباً» needed 50 views
 * while the most-viewed listing on the site had 34, and the search cards never
 * carried marks at all. They also measured views under a «most requested»
 * label; each mark now counts the thing it names:
 *
 * - «الأكثر طلباً» (hot): enquiries sent about the listing.
 * - «الأكثر إعجاباً» (mostLiked): times it was saved to a favourites list.
 * - «الأكثر مشاهدة» (mostViewed): page views.
 * - «جديد» (new): listed in the last two weeks.
 *
 * The thresholds are plain counts sized to the traffic of October 2026 (the
 * top listings had 20-34 views, and none had a save or an enquiry yet). Raise
 * them here, and only here, as the platform grows.
 */

export const HOT_MIN_INQUIRIES = 3;
export const MOST_LIKED_MIN_FAVORITES = 3;
export const MOST_VIEWED_MIN_VIEWS = 20;
export const NEW_LISTING_DAYS = 14;

export type EarnedBadge = 'hot' | 'mostLiked' | 'mostViewed' | 'new';
export type ListingBadge = 'featured' | EarnedBadge;

export interface ListingBadgeInput {
  featured?: boolean;
  /** The frontend status: 'available' | 'pending' | 'sold' | 'rented'. */
  status?: string;
  viewCount?: number;
  favoriteCount?: number;
  inquiryCount?: number;
  /** ISO date the listing was published. */
  dateAdded?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function isNewListing(dateAdded: string | undefined, now: number = Date.now()): boolean {
  if (!dateAdded) return false;
  const added = new Date(dateAdded).getTime();
  if (Number.isNaN(added) || added > now) return false;
  return now - added < NEW_LISTING_DAYS * DAY_MS;
}

/** The marks a listing has earned, strongest first: asked about, saved, viewed, new. */
export function earnedBadges(listing: ListingBadgeInput, now: number = Date.now()): EarnedBadge[] {
  // A sold or rented listing is off the market; a popularity mark would only tease.
  const status = (listing.status || '').toLowerCase();
  if (status === 'sold' || status === 'rented') return [];
  const earned: EarnedBadge[] = [];
  if ((listing.inquiryCount ?? 0) >= HOT_MIN_INQUIRIES) earned.push('hot');
  if ((listing.favoriteCount ?? 0) >= MOST_LIKED_MIN_FAVORITES) earned.push('mostLiked');
  if ((listing.viewCount ?? 0) >= MOST_VIEWED_MIN_VIEWS) earned.push('mostViewed');
  if (isNewListing(listing.dateAdded, now)) earned.push('new');
  return earned;
}

/**
 * «مميز» first, then the earned marks. `earnedLimit` caps the earned ones: a
 * card shows its strongest one (cards stay calm), the property page shows all.
 */
export function listingBadges(
  listing: ListingBadgeInput,
  { earnedLimit = Infinity, now = Date.now() }: { earnedLimit?: number; now?: number } = {},
): ListingBadge[] {
  const earned = earnedBadges(listing, now).slice(0, earnedLimit);
  return listing.featured ? ['featured', ...earned] : earned;
}
